/**
 * Fonzie Drawer Builder - v1.0
 * Full-screen slide-up drawer with 4-step car kit builder
 * Triggered by URL hash (#fonzie-builder) or query param (?builder=fonzie)
 */

class FonzieDrawerBuilder {
  constructor(config = {}) {
    this.engine = new FonzieEngine({ mode: 'drawer', ...config });
    this.drawer = null;
    this.currentStep = 0;
    this.maxSteps = 4; // 0: Color, 1: Size, 2: Sprayer, 3: Finish
    this.isOpen = false;
    
    // Load configuration from customizer if available
    this.config = this.loadCustomizerConfig();
    this.messages = {};
    this.appearance = {};
    
    // Apply config to engine
    if (this.config) {
      this.applyConfiguration();
    }
    
    this.init();
  }
  
  /**
   * Load configuration from Shopify customizer
   */
  loadCustomizerConfig() {
    const configEl = document.getElementById('fonzie-builder-config');
    if (!configEl) {
      return null;
    }
    
    try {
      const config = JSON.parse(configEl.textContent);
      console.log('✅ Fonzie: Customizer config loaded');
      return config;
    } catch (e) {
      console.error('❌ Fonzie: Failed to parse config', e);
      return null;
    }
  }
  
  /**
   * Apply configuration from customizer
   */
  applyConfiguration() {
    if (!this.config) return;
    
    // Apply video URLs
    if (this.config.videos) {
      this.engine.videos = {
        ...this.engine.videos,
        ...this.config.videos
      };
    }
    
    // Store messages for use in UI
    this.messages = this.config.messages || {};
    
    // Store appearance settings
    this.appearance = this.config.appearance || {};
    
    console.log('✅ Fonzie: Configuration applied');
  }
  
  /**
   * Get Fonzie avatar image URL
   */
  getAvatarImageUrl() {
    // Try to get from Shopify assets
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cdn/shop/t/6/assets/fonzie-avatar-3d.png';
    }
    // Fallback
    return '/assets/fonzie-avatar-3d.png';
  }
  
  /**
   * Initialize drawer builder
   */
  init() {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  /**
   * Setup drawer builder
   */
  setup() {
    console.log('🚀 Fonzie Drawer Builder: Initializing...');
    
    // Create drawer element
    this.createDrawer();
    
    // Listen for hash/query changes
    this.listenForTriggers();
    
    // Check if should auto-open
    this.checkAutoOpen();
    
    console.log('✅ Fonzie Drawer Builder: Ready');
  }
  
  /**
   * Create drawer HTML structure
   */
  createDrawer() {
    // Check if drawer already exists
    if (document.getElementById('fonzie-builder-drawer')) {
      this.drawer = document.getElementById('fonzie-builder-drawer');
      return;
    }
    
    // Create drawer container
    this.drawer = document.createElement('div');
    this.drawer.id = 'fonzie-builder-drawer';
    this.drawer.className = 'fonzie-builder-drawer';
    this.drawer.setAttribute('data-state', 'closed');
    this.drawer.setAttribute('role', 'dialog');
    this.drawer.setAttribute('aria-modal', 'true');
    this.drawer.setAttribute('aria-labelledby', 'fonzie-builder-title');
    
    this.drawer.innerHTML = `
      <div class="fonzie-builder-overlay"></div>
      
      <div class="fonzie-builder-content">
        <!-- Header -->
        <div class="fonzie-builder-header">
          <h2 class="fonzie-builder-title" id="fonzie-builder-title">Build Your Car Kit with Fonzie</h2>
          <button class="fonzie-builder-close" type="button" aria-label="Close builder">×</button>
        </div>
        
        <!-- Progress Bar -->
        <div class="fonzie-progress-bar">
          <span class="fonzie-progress-dot active" data-step="0"></span>
          <span class="fonzie-progress-dot" data-step="1"></span>
          <span class="fonzie-progress-dot" data-step="2"></span>
          <span class="fonzie-progress-dot" data-step="3"></span>
        </div>
        
        <!-- Body (Scrollable) -->
        <div class="fonzie-builder-body">
          <!-- Steps will be injected here -->
          <div class="fonzie-step active" data-step="0" id="fonzie-step-color">
            <!-- Fonzie Video Section -->
            <div class="fonzie-step-video-section" data-video-key="color">
              <div class="fonzie-video-container">
                <div class="fonzie-video-player-wrapper">
                  <video class="fonzie-inline-video" id="fonzie-video-color" playsinline preload="metadata">
                    <source src="" type="video/mp4">
                  </video>
                  <div class="fonzie-video-placeholder" id="fonzie-placeholder-color" data-message-key="color">
                    <div class="fonzie-avatar-small">
                      <img src="" alt="Fonzie" class="fonzie-avatar-img" data-avatar="true" />
                    </div>
                    <div class="fonzie-placeholder-message">
                      <strong data-title="color">Hey! I'm Fonzie.</strong>
                      <p data-message="color">We've got over 100 colors! ColorShift changes color in different lighting - my favorite! Take your time and find the one that speaks to you.</p>
                    </div>
                  </div>
                </div>
                <button class="fonzie-watch-video-btn" data-step-video="color" type="button">
                  ▶ Watch Fonzie Explain
                </button>
                <button class="fonzie-skip-intro-btn" type="button" style="display: none;">
                  Skip & Choose Color ↓
                </button>
              </div>
            </div>
            
            <div class="fonzie-step-header">
              <h3 class="fonzie-step-title">Choose Your Color</h3>
              <p class="fonzie-step-description">Select from over 100 colors including Solid, Metallic, ColorShift, HyperShift, and Holographic finishes.</p>
            </div>
            <div class="fonzie-loading">Loading colors...</div>
          </div>
          
          <div class="fonzie-step" data-step="1" id="fonzie-step-size">
            <!-- Fonzie Video Section -->
            <div class="fonzie-step-video-section" data-video-key="size">
              <div class="fonzie-video-container">
                <div class="fonzie-video-player-wrapper">
                  <video class="fonzie-inline-video" id="fonzie-video-size" playsinline preload="metadata">
                    <source src="" type="video/mp4">
                  </video>
                  <div class="fonzie-video-placeholder" id="fonzie-placeholder-size" data-message-key="size">
                    <div class="fonzie-avatar-small">
                      <img src="" alt="Fonzie" class="fonzie-avatar-img" data-avatar="true" />
                    </div>
                    <div class="fonzie-placeholder-message">
                      <strong data-title="size">Size matters!</strong>
                      <p data-message="size">Small is for ATVs and Slingshots. Medium for sedans. Large for SUVs. XL for trucks. Pro tip: When in doubt, size up!</p>
                    </div>
                  </div>
                </div>
                <button class="fonzie-watch-video-btn" data-step-video="size" type="button">
                  ▶ Watch Fonzie Explain
                </button>
                <button class="fonzie-skip-intro-btn" type="button" style="display: none;">
                  Skip & Choose Size ↓
                </button>
              </div>
            </div>
            
            <div class="fonzie-step-header">
              <h3 class="fonzie-step-title">Select Your Size</h3>
              <p class="fonzie-step-description">Choose the kit size based on your vehicle type. When in doubt, size up!</p>
            </div>
            <div class="fonzie-options-grid size-grid" id="fonzie-size-options"></div>
          </div>
          
          <div class="fonzie-step" data-step="2" id="fonzie-step-sprayer">
            <!-- Fonzie Video Section -->
            <div class="fonzie-step-video-section" data-video-key="sprayer">
              <div class="fonzie-video-container">
                <div class="fonzie-video-player-wrapper">
                  <video class="fonzie-inline-video" id="fonzie-video-sprayer" playsinline preload="metadata">
                    <source src="" type="video/mp4">
                  </video>
                  <div class="fonzie-video-placeholder" id="fonzie-placeholder-sprayer" data-message-key="sprayer">
                    <div class="fonzie-avatar-small">
                      <img src="" alt="Fonzie" class="fonzie-avatar-img" data-avatar="true" />
                    </div>
                    <div class="fonzie-placeholder-message">
                      <strong data-title="sprayer">Need a sprayer?</strong>
                      <p data-message="sprayer">The G-Force is perfect for DIYers. Want the best finish? Go Ultimate. Already have equipment? Select "No Thanks"!</p>
                    </div>
                  </div>
                </div>
                <button class="fonzie-watch-video-btn" data-step-video="sprayer" type="button">
                  ▶ Watch Fonzie Explain
                </button>
                <button class="fonzie-skip-intro-btn" type="button" style="display: none;">
                  Skip & Choose Sprayer ↓
                </button>
              </div>
            </div>
            
            <div class="fonzie-step-header">
              <h3 class="fonzie-step-title">Need a Sprayer?</h3>
              <p class="fonzie-step-description">Choose a professional sprayer system or skip if you already have one.</p>
            </div>
            <div class="fonzie-options-grid sprayer-grid" id="fonzie-sprayer-options"></div>
          </div>
          
          <div class="fonzie-step" data-step="3" id="fonzie-step-finish">
            <!-- Fonzie Video Section -->
            <div class="fonzie-step-video-section" data-video-key="finish">
              <div class="fonzie-video-container">
                <div class="fonzie-video-player-wrapper">
                  <video class="fonzie-inline-video" id="fonzie-video-finish" playsinline preload="metadata">
                    <source src="" type="video/mp4">
                  </video>
                  <div class="fonzie-video-placeholder" id="fonzie-placeholder-finish" data-message-key="finish">
                    <div class="fonzie-avatar-small">
                      <img src="" alt="Fonzie" class="fonzie-avatar-img" data-avatar="true" />
                    </div>
                    <div class="fonzie-placeholder-message">
                      <strong data-title="finish">Last step!</strong>
                      <p data-message="finish">Standard gives that classic matte look. Ultra High Gloss? That's the premium wet finish that looks INSANE. Both are great!</p>
                    </div>
                  </div>
                </div>
                <button class="fonzie-watch-video-btn" data-step-video="finish" type="button">
                  ▶ Watch Fonzie Explain
                </button>
                <button class="fonzie-skip-intro-btn" type="button" style="display: none;">
                  Skip & Choose Finish ↓
                </button>
              </div>
            </div>
            
            <div class="fonzie-step-header">
              <h3 class="fonzie-step-title">Choose Finish</h3>
              <p class="fonzie-step-description">Select between classic matte or premium ultra high gloss finish.</p>
            </div>
            <div class="fonzie-options-grid finish-grid" id="fonzie-finish-options"></div>
          </div>
          
          <div class="fonzie-step" data-step="4" id="fonzie-step-summary">
            <div class="fonzie-step-header">
              <h3 class="fonzie-step-title">Your Custom Kit</h3>
              <p class="fonzie-step-description">Review your selections and add to cart.</p>
            </div>
            <div id="fonzie-summary-content"></div>
          </div>
        </div>
        
        <!-- Footer (Navigation) -->
        <div class="fonzie-builder-footer">
          <button class="fonzie-back-btn" type="button" aria-label="Go back" style="display: none;">
            ←
          </button>
          <button class="fonzie-next-btn" type="button" disabled>
            Continue
          </button>
        </div>
        
        <!-- Loading Overlay -->
        <div class="fonzie-loading-overlay">
          <div class="fonzie-spinner"></div>
        </div>
      </div>
    `;
    
    document.body.appendChild(this.drawer);
    
    // Apply customizer messages if available
    this.applyCustomizerMessages();
    
    // Update avatar images to use real Fonzie
    this.updateAvatarImages();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Load stylesheet
    this.loadStylesheet();
  }
  
  /**
   * Update avatar images in video placeholders
   */
  updateAvatarImages() {
    const avatarUrl = this.getAvatarImageUrl();
    const avatarImages = this.drawer.querySelectorAll('img[data-avatar="true"]');
    
    avatarImages.forEach(img => {
      img.src = avatarUrl;
    });
    
    console.log('✅ Fonzie: Avatar images updated to', avatarUrl);
  }
  
  /**
   * Apply customizer messages to placeholder content
   */
  applyCustomizerMessages() {
    if (!this.messages || Object.keys(this.messages).length === 0) {
      console.log('ℹ️ Fonzie: Using default messages');
      return;
    }
    
    console.log('✅ Fonzie: Applying customizer messages');
    
    // Update each step's placeholder messages
    const steps = ['color', 'size', 'sprayer', 'finish'];
    
    steps.forEach(step => {
      const titleKey = step + 'Title';
      const messageKey = step + 'Message';
      
      if (this.messages[titleKey]) {
        const titleEl = this.drawer.querySelector(`[data-title="${step}"]`);
        if (titleEl) {
          titleEl.textContent = this.messages[titleKey];
        }
      }
      
      if (this.messages[messageKey]) {
        const messageEl = this.drawer.querySelector(`[data-message="${step}"]`);
        if (messageEl) {
          messageEl.textContent = this.messages[messageKey];
        }
      }
    });
  }
  
  /**
   * Load drawer builder stylesheet
   */
  loadStylesheet() {
    if (document.querySelector('link[href*="fonzie-drawer-builder.css"]')) {
      return; // Already loaded
    }
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = window.Shopify?.routes?.root 
      ? window.Shopify.routes.root + 'cdn/shop/t/6/assets/fonzie-drawer-builder.css'
      : '/assets/fonzie-drawer-builder.css';
    link.media = 'print';
    link.onload = function() { this.media = 'all'; };
    document.head.appendChild(link);
  }
  
  /**
   * Setup event listeners
   */
  setupEventListeners() {
    if (!this.drawer) return;
    
    // Close button
    const closeBtn = this.drawer.querySelector('.fonzie-builder-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }
    
    // Overlay click
    const overlay = this.drawer.querySelector('.fonzie-builder-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }
    
    // Back button
    const backBtn = this.drawer.querySelector('.fonzie-back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', () => this.previousStep());
    }
    
    // Next button
    const nextBtn = this.drawer.querySelector('.fonzie-next-btn');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.nextStep());
    }
    
    // Keyboard: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Progress dots (click to jump to step if completed)
    const dots = this.drawer.querySelectorAll('.fonzie-progress-dot');
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const step = parseInt(dot.getAttribute('data-step'));
        if (step < this.currentStep || dot.classList.contains('completed')) {
          this.goToStep(step);
        }
      });
    });
    
    // Watch video buttons (inline videos at each step)
    const watchVideoBtns = this.drawer.querySelectorAll('.fonzie-watch-video-btn');
    watchVideoBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const videoKey = btn.getAttribute('data-step-video');
        this.playInlineVideo(videoKey);
      });
    });
    
    // Skip intro buttons
    const skipBtns = this.drawer.querySelectorAll('.fonzie-skip-intro-btn');
    skipBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.hideVideoSection();
      });
    });
  }
  
  /**
   * Listen for hash/query triggers
   */
  listenForTriggers() {
    // Listen for hash changes
    window.addEventListener('hashchange', () => {
      this.checkAutoOpen();
    });
    
    // Listen for popstate (back/forward)
    window.addEventListener('popstate', () => {
      this.checkAutoOpen();
    });
  }
  
  /**
   * Check if should auto-open
   */
  checkAutoOpen() {
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    
    if (hash === '#fonzie-builder' || params.get('builder') === 'fonzie') {
      if (!this.isOpen) {
        this.open();
      }
    } else {
      if (this.isOpen) {
        this.close();
      }
    }
  }
  
  /**
   * Open drawer
   */
  async open() {
    if (this.isOpen || !this.drawer) return;
    
    console.log('🚀 Fonzie Drawer Builder: Opening...');
    
    // Load products if not loaded
    if (!this.engine.isLoaded) {
      this.showLoading();
      await this.engine.loadCarKitProducts();
      this.hideLoading();
    }
    
    // Build color grid
    this.buildColorGrid();
    
    // Build size options
    this.buildSizeOptions();
    
    // Build sprayer options
    this.buildSprayerOptions();
    
    // Build finish options
    this.buildFinishOptions();
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    
    // Open drawer
    this.drawer.setAttribute('data-state', 'open');
    this.isOpen = true;
    
    // Go to step 0
    this.goToStep(0);
    
    // Track
    this.engine.trackEvent('builder_opened', 'hash_url');
    
    console.log('✅ Fonzie Drawer Builder: Opened');
  }
  
  /**
   * Play inline video at current step
   */
  playInlineVideo(videoKey) {
    const currentStepEl = this.drawer.querySelector('.fonzie-step.active');
    if (!currentStepEl) return;
    
    const videoSection = currentStepEl.querySelector('.fonzie-step-video-section');
    const video = currentStepEl.querySelector('.fonzie-inline-video');
    const placeholder = currentStepEl.querySelector('.fonzie-video-placeholder');
    const watchBtn = currentStepEl.querySelector('.fonzie-watch-video-btn');
    const skipBtn = currentStepEl.querySelector('.fonzie-skip-intro-btn');
    
    if (!video || !videoSection) return;
    
    // Get video URL from engine
    const videoUrl = this.engine.videos[videoKey];
    const source = video.querySelector('source');
    
    if (!videoUrl || videoUrl === '') {
      // No video URL - just hide placeholder and show skip button
      console.log('ℹ️ No video URL for', videoKey, '- using placeholder');
      if (watchBtn) watchBtn.style.display = 'none';
      if (skipBtn) {
        skipBtn.style.display = 'block';
        skipBtn.textContent = 'Continue ↓';
      }
      return;
    }
    
    // Load and play video
    if (source) {
      source.src = videoUrl;
      video.load();
    }
    
    // Hide placeholder, show video
    if (placeholder) placeholder.style.display = 'none';
    video.style.display = 'block';
    if (watchBtn) watchBtn.style.display = 'none';
    
    // Play video
    video.play().catch(err => {
      console.warn('⚠️ Video autoplay prevented:', err);
      // Show play button overlay if autoplay blocked
    });
    
    // Show skip button after 5 seconds
    setTimeout(() => {
      if (skipBtn) skipBtn.style.display = 'block';
    }, 5000);
    
    // When video ends, hide video section automatically
    video.addEventListener('ended', () => {
      this.hideVideoSection();
      this.engine.trackEvent('step_video_completed', videoKey);
    }, { once: true });
    
    // Track
    this.engine.trackEvent('step_video_played', videoKey);
  }
  
  /**
   * Hide video section and show step content
   */
  hideVideoSection() {
    const currentStepEl = this.drawer.querySelector('.fonzie-step.active');
    if (!currentStepEl) return;
    
    const videoSection = currentStepEl.querySelector('.fonzie-step-video-section');
    const video = currentStepEl.querySelector('.fonzie-inline-video');
    
    if (videoSection) {
      videoSection.style.display = 'none';
    }
    
    // Pause video
    if (video) {
      video.pause();
    }
    
    // Scroll to step content
    const stepHeader = currentStepEl.querySelector('.fonzie-step-header');
    if (stepHeader) {
      stepHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  
  /**
   * Close drawer
   */
  close() {
    if (!this.isOpen || !this.drawer) return;
    
    console.log('🚀 Fonzie Drawer Builder: Closing...');
    
    // Close drawer
    this.drawer.setAttribute('data-state', 'closed');
    
    // Restore body scroll
    const scrollY = document.body.style.top;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    
    this.isOpen = false;
    
    // Remove hash from URL if present
    if (window.location.hash === '#fonzie-builder') {
      history.pushState('', document.title, window.location.pathname + window.location.search);
    }
    
    // Track
    this.engine.trackEvent('builder_closed', 'user_action');
    
    console.log('✅ Fonzie Drawer Builder: Closed');
  }
  
  /**
   * Go to specific step
   */
  goToStep(stepNum) {
    if (stepNum < 0 || stepNum > this.maxSteps) return;
    
    console.log('🚀 Fonzie Drawer Builder: Going to step', stepNum);
    
    // Pause any playing videos from previous step
    const currentStepEl = this.drawer.querySelector('.fonzie-step.active');
    if (currentStepEl) {
      const video = currentStepEl.querySelector('.fonzie-inline-video');
      if (video) {
        video.pause();
      }
    }
    
    this.currentStep = stepNum;
    this.engine.currentStep = stepNum;
    
    // Update steps visibility
    const steps = this.drawer.querySelectorAll('.fonzie-step');
    steps.forEach((step, index) => {
      if (index === stepNum) {
        step.classList.add('active');
      } else {
        step.classList.remove('active');
      }
    });
    
    // Update progress dots
    const dots = this.drawer.querySelectorAll('.fonzie-progress-dot');
    dots.forEach((dot, index) => {
      dot.classList.remove('active', 'completed');
      if (index < stepNum) {
        dot.classList.add('completed');
      } else if (index === stepNum) {
        dot.classList.add('active');
      }
    });
    
    // Update navigation buttons
    this.updateNavigation();
    
    // Load video for this step (but don't auto-play)
    this.prepareStepVideo(stepNum);
    
    // Track
    this.engine.trackEvent('step_viewed', stepNum);
  }
  
  /**
   * Prepare video for current step
   */
  prepareStepVideo(stepNum) {
    const currentStepEl = this.drawer.querySelector('.fonzie-step.active');
    if (!currentStepEl) return;
    
    const videoSection = currentStepEl.querySelector('.fonzie-step-video-section');
    if (!videoSection) return;
    
    const videoKey = videoSection.getAttribute('data-video-key');
    const video = currentStepEl.querySelector('.fonzie-inline-video');
    const source = video ? video.querySelector('source') : null;
    
    // Preload video URL if available
    if (videoKey && source && this.engine.videos[videoKey]) {
      source.src = this.engine.videos[videoKey];
      // Don't load yet - just set src for faster playback when clicked
    }
  }
  
  /**
   * Next step
   */
  nextStep() {
    if (this.currentStep < this.maxSteps) {
      this.goToStep(this.currentStep + 1);
    } else {
      // Last step - add to cart
      this.addToCart();
    }
  }
  
  /**
   * Previous step
   */
  previousStep() {
    if (this.currentStep > 0) {
      this.goToStep(this.currentStep - 1);
    }
  }
  
  /**
   * Update navigation buttons
   */
  updateNavigation() {
    const backBtn = this.drawer.querySelector('.fonzie-back-btn');
    const nextBtn = this.drawer.querySelector('.fonzie-next-btn');
    
    if (!backBtn || !nextBtn) return;
    
    // Show/hide back button
    if (this.currentStep === 0) {
      backBtn.style.display = 'none';
    } else {
      backBtn.style.display = 'flex';
    }
    
    // Update next button state and text
    let isEnabled = false;
    let buttonText = 'Continue';
    
    switch (this.currentStep) {
      case 0: // Color
        isEnabled = !!this.engine.selections.colorProduct;
        break;
      case 1: // Size
        isEnabled = !!this.engine.selections.size;
        break;
      case 2: // Sprayer
        isEnabled = !!this.engine.selections.sprayer;
        break;
      case 3: // Finish
        isEnabled = !!this.engine.selections.finish;
        break;
      case 4: // Summary
        isEnabled = true;
        buttonText = 'Add to Cart';
        break;
    }
    
    nextBtn.disabled = !isEnabled;
    nextBtn.textContent = buttonText;
    
    // Hide next button on summary (show add to cart in summary instead)
    if (this.currentStep === 4) {
      nextBtn.style.display = 'none';
    } else {
      nextBtn.style.display = 'flex';
    }
  }
  
  /**
   * Build color selection grid
   */
  buildColorGrid() {
    const colorStep = document.getElementById('fonzie-step-color');
    if (!colorStep) return;
    
    // Remove loading indicator
    const loading = colorStep.querySelector('.fonzie-loading');
    if (loading) {
      loading.remove();
    }
    
    // Check if already built
    if (colorStep.querySelector('.fonzie-color-selection')) {
      return;
    }
    
    // Create color selection UI
    const colorSelection = document.createElement('div');
    colorSelection.className = 'fonzie-color-selection';
    
    // Build category swatches
    const categories = document.createElement('div');
    categories.className = 'fonzie-color-categories';
    
    const allCount = this.engine.carKitProducts.length;
    const categoryButtons = [
      { key: 'all', label: 'All Colors', count: allCount },
      { key: 'solid', label: 'Solid', count: this.engine.colorGroups.solid.length },
      { key: 'metallic', label: 'Metallic', count: this.engine.colorGroups.metallic.length },
      { key: 'colorshift', label: 'ColorShift', count: this.engine.colorGroups.colorshift.length },
      { key: 'hypershift', label: 'HyperShift', count: this.engine.colorGroups.hypershift.length },
      { key: 'holographic', label: 'Holographic', count: this.engine.colorGroups.holographic.length }
    ];
    
    categoryButtons.forEach(cat => {
      if (cat.count === 0 && cat.key !== 'all') return; // Skip empty categories
      
      const btn = document.createElement('button');
      btn.className = 'fonzie-category-swatch';
      btn.type = 'button';
      btn.setAttribute('data-category', cat.key);
      btn.setAttribute('data-active', cat.key === 'all' ? 'true' : 'false');
      btn.innerHTML = `${cat.label} <span class="count">(${cat.count})</span>`;
      
      btn.addEventListener('click', () => {
        // Update active state
        categories.querySelectorAll('.fonzie-category-swatch').forEach(b => {
          b.setAttribute('data-active', 'false');
        });
        btn.setAttribute('data-active', 'true');
        
        // Filter grid
        this.filterColorGrid(cat.key);
      });
      
      categories.appendChild(btn);
    });
    
    colorSelection.appendChild(categories);
    
    // Build search bar
    const searchContainer = document.createElement('div');
    searchContainer.className = 'fonzie-color-search';
    const searchInput = document.createElement('input');
    searchInput.type = 'search';
    searchInput.placeholder = 'Search colors...';
    searchInput.setAttribute('aria-label', 'Search car kit colors');
    searchInput.addEventListener('input', (e) => {
      this.searchColors(e.target.value);
    });
    searchContainer.appendChild(searchInput);
    colorSelection.appendChild(searchContainer);
    
    // Build color grid
    const colorGrid = document.createElement('div');
    colorGrid.className = 'fonzie-color-grid';
    colorGrid.setAttribute('data-category-filter', 'all');
    
    // Populate with all colors
    this.engine.carKitProducts.forEach(product => {
      const card = this.createColorCard(product);
      colorGrid.appendChild(card);
    });
    
    colorSelection.appendChild(colorGrid);
    
    colorStep.appendChild(colorSelection);
  }
  
  /**
   * Create color card element
   */
  createColorCard(product) {
    const card = document.createElement('button');
    card.className = 'fonzie-color-card';
    card.type = 'button';
    card.setAttribute('data-product-handle', product.handle);
    card.setAttribute('data-category', product.category || 'solid');
    
    card.innerHTML = `
      <div class="fonzie-color-image">
        <img src="${product.featuredImage || product.image}" alt="${product.title}" loading="lazy">
      </div>
      <div class="fonzie-color-info">
        <span class="fonzie-color-name">${product.title}</span>
        <span class="fonzie-color-badge">${product.category || 'Solid'}</span>
      </div>
    `;
    
    card.addEventListener('click', () => {
      this.selectColor(product);
    });
    
    return card;
  }
  
  /**
   * Select color
   */
  selectColor(product) {
    console.log('🎨 Selected color:', product.title);
    
    // Update engine selection
    this.engine.selections.color = product.title;
    this.engine.selections.colorProduct = product;
    
    // Update UI
    const colorGrid = this.drawer.querySelector('.fonzie-color-grid');
    if (colorGrid) {
      colorGrid.querySelectorAll('.fonzie-color-card').forEach(card => {
        card.classList.remove('selected');
      });
      
      const selectedCard = colorGrid.querySelector(`[data-product-handle="${product.handle}"]`);
      if (selectedCard) {
        selectedCard.classList.add('selected');
      }
    }
    
    // Enable next button
    this.updateNavigation();
    
    // Track
    this.engine.trackEvent('color_selected', product.title, {
      category: product.category,
      handle: product.handle
    });
    
    // Auto-advance after short delay
    setTimeout(() => {
      this.nextStep();
    }, 300);
  }
  
  /**
   * Filter color grid by category
   */
  filterColorGrid(category) {
    const colorGrid = this.drawer.querySelector('.fonzie-color-grid');
    if (!colorGrid) return;
    
    colorGrid.setAttribute('data-category-filter', category);
    
    const cards = colorGrid.querySelectorAll('.fonzie-color-card');
    cards.forEach(card => {
      const cardCategory = card.getAttribute('data-category');
      if (category === 'all' || cardCategory === category) {
        card.style.display = 'flex';
      } else {
        card.style.display = 'none';
      }
    });
  }
  
  /**
   * Search colors by name
   */
  searchColors(query) {
    const colorGrid = this.drawer.querySelector('.fonzie-color-grid');
    if (!colorGrid) return;
    
    const lowerQuery = query.toLowerCase();
    
    const cards = colorGrid.querySelectorAll('.fonzie-color-card');
    cards.forEach(card => {
      const name = card.querySelector('.fonzie-color-name').textContent.toLowerCase();
      if (name.includes(lowerQuery)) {
        card.style.display = 'flex';
      } else {
        card.style.display = 'none';
      }
    });
  }
  
  /**
   * Build size options
   */
  buildSizeOptions() {
    const sizeContainer = document.getElementById('fonzie-size-options');
    if (!sizeContainer || sizeContainer.children.length > 0) return;
    
    const sizes = [
      {
        value: 'Small',
        label: 'Small',
        description: 'ATV, Slingshot, 4-Wheeler',
        image: 'https://cdn.shopify.com/s/files/1/[store]/small.webp'
      },
      {
        value: 'Medium',
        label: 'Medium',
        description: 'Mustang, BMW 3 Series',
        image: 'https://cdn.shopify.com/s/files/1/[store]/medium.webp'
      },
      {
        value: 'Large',
        label: 'Large',
        description: 'Audi A7, BMW 5 Series',
        image: 'https://cdn.shopify.com/s/files/1/[store]/large.webp'
      },
      {
        value: 'XL',
        label: 'Extra Large',
        description: 'Ford F-150, Chevy Tahoe',
        image: 'https://cdn.shopify.com/s/files/1/[store]/xl.webp'
      }
    ];
    
    sizes.forEach(size => {
      const card = document.createElement('button');
      card.className = 'fonzie-option-card';
      card.type = 'button';
      card.setAttribute('data-value', size.value);
      
      card.innerHTML = `
        <div class="fonzie-option-image">
          <img src="${size.image}" alt="${size.label}" loading="lazy">
        </div>
        <div class="fonzie-option-label">${size.label}</div>
        <div class="fonzie-option-description">${size.description}</div>
      `;
      
      card.addEventListener('click', () => {
        this.selectSize(size.value);
      });
      
      sizeContainer.appendChild(card);
    });
  }
  
  /**
   * Select size
   */
  selectSize(value) {
    console.log('📏 Selected size:', value);
    
    this.engine.selections.size = value;
    
    // Update UI
    const sizeContainer = document.getElementById('fonzie-size-options');
    if (sizeContainer) {
      sizeContainer.querySelectorAll('.fonzie-option-card').forEach(card => {
        card.classList.remove('selected');
      });
      
      const selectedCard = sizeContainer.querySelector(`[data-value="${value}"]`);
      if (selectedCard) {
        selectedCard.classList.add('selected');
      }
    }
    
    this.updateNavigation();
    
    this.engine.trackEvent('size_selected', value);
    
    setTimeout(() => {
      this.nextStep();
    }, 300);
  }
  
  /**
   * Build sprayer options
   */
  buildSprayerOptions() {
    const sprayerContainer = document.getElementById('fonzie-sprayer-options');
    if (!sprayerContainer || sprayerContainer.children.length > 0) return;
    
    const sprayers = [
      {
        value: 'G-Force Sprayer',
        label: 'DYC G-Force',
        description: 'Perfect for DIYers',
        image: 'https://cdn.shopify.com/s/files/1/[store]/gforce.webp'
      },
      {
        value: 'Ultimate Sprayer',
        label: 'DYC Ultimate',
        description: 'Best finish quality',
        image: 'https://cdn.shopify.com/s/files/1/[store]/ultimate.webp'
      },
      {
        value: 'No Sprayer',
        label: 'No Thanks',
        description: 'I already have one',
        image: 'https://cdn.shopify.com/s/files/1/[store]/no-sprayer.webp'
      }
    ];
    
    sprayers.forEach(sprayer => {
      const card = document.createElement('button');
      card.className = 'fonzie-option-card';
      card.type = 'button';
      card.setAttribute('data-value', sprayer.value);
      
      card.innerHTML = `
        <div class="fonzie-option-image">
          <img src="${sprayer.image}" alt="${sprayer.label}" loading="lazy">
        </div>
        <div class="fonzie-option-label">${sprayer.label}</div>
        <div class="fonzie-option-description">${sprayer.description}</div>
      `;
      
      card.addEventListener('click', () => {
        this.selectSprayer(sprayer.value);
      });
      
      sprayerContainer.appendChild(card);
    });
  }
  
  /**
   * Select sprayer
   */
  selectSprayer(value) {
    console.log('🔫 Selected sprayer:', value);
    
    this.engine.selections.sprayer = value;
    
    const sprayerContainer = document.getElementById('fonzie-sprayer-options');
    if (sprayerContainer) {
      sprayerContainer.querySelectorAll('.fonzie-option-card').forEach(card => {
        card.classList.remove('selected');
      });
      
      const selectedCard = sprayerContainer.querySelector(`[data-value="${value}"]`);
      if (selectedCard) {
        selectedCard.classList.add('selected');
      }
    }
    
    this.updateNavigation();
    
    this.engine.trackEvent('sprayer_selected', value);
    
    setTimeout(() => {
      this.nextStep();
    }, 300);
  }
  
  /**
   * Build finish options
   */
  buildFinishOptions() {
    const finishContainer = document.getElementById('fonzie-finish-options');
    if (!finishContainer || finishContainer.children.length > 0) return;
    
    const finishes = [
      {
        value: 'Standard',
        label: 'Standard',
        description: 'Classic matte finish',
        image: 'https://cdn.shopify.com/s/files/1/[store]/standard.webp'
      },
      {
        value: 'Ultra High Gloss',
        label: 'Ultra High Gloss',
        description: 'Premium wet finish',
        image: 'https://cdn.shopify.com/s/files/1/[store]/uhg.webp'
      }
    ];
    
    finishes.forEach(finish => {
      const card = document.createElement('button');
      card.className = 'fonzie-option-card';
      card.type = 'button';
      card.setAttribute('data-value', finish.value);
      
      card.innerHTML = `
        <div class="fonzie-option-image">
          <img src="${finish.image}" alt="${finish.label}" loading="lazy">
        </div>
        <div class="fonzie-option-label">${finish.label}</div>
        <div class="fonzie-option-description">${finish.description}</div>
      `;
      
      card.addEventListener('click', () => {
        this.selectFinish(finish.value);
      });
      
      finishContainer.appendChild(card);
    });
  }
  
  /**
   * Select finish
   */
  selectFinish(value) {
    console.log('✨ Selected finish:', value);
    
    this.engine.selections.finish = value;
    
    const finishContainer = document.getElementById('fonzie-finish-options');
    if (finishContainer) {
      finishContainer.querySelectorAll('.fonzie-option-card').forEach(card => {
        card.classList.remove('selected');
      });
      
      const selectedCard = finishContainer.querySelector(`[data-value="${value}"]`);
      if (selectedCard) {
        selectedCard.classList.add('selected');
      }
    }
    
    this.updateNavigation();
    
    this.engine.trackEvent('finish_selected', value);
    
    // Build summary and advance
    setTimeout(() => {
      this.buildSummary();
      this.goToStep(4);
    }, 300);
  }
  
  /**
   * Build summary
   */
  buildSummary() {
    const summaryContainer = document.getElementById('fonzie-summary-content');
    if (!summaryContainer) return;
    
    const summary = this.engine.getSummary();
    const variant = summary.variant;
    
    summaryContainer.innerHTML = `
      <div class="fonzie-summary-preview">
        <img src="${this.engine.selections.colorProduct?.featuredImage || this.engine.selections.colorProduct?.image}" alt="${summary.color}">
      </div>
      
      <div class="fonzie-summary-cards">
        <button class="fonzie-summary-card" type="button" data-edit-step="0">
          <div>
            <span class="label">Color</span>
            <span class="value">${summary.color}</span>
          </div>
          <span class="edit-icon">✎</span>
        </button>
        
        <button class="fonzie-summary-card" type="button" data-edit-step="1">
          <div>
            <span class="label">Size</span>
            <span class="value">${summary.size}</span>
          </div>
          <span class="edit-icon">✎</span>
        </button>
        
        <button class="fonzie-summary-card" type="button" data-edit-step="2">
          <div>
            <span class="label">Sprayer</span>
            <span class="value">${summary.sprayer}</span>
          </div>
          <span class="edit-icon">✎</span>
        </button>
        
        <button class="fonzie-summary-card" type="button" data-edit-step="3">
          <div>
            <span class="label">Finish</span>
            <span class="value">${summary.finish}</span>
          </div>
          <span class="edit-icon">✎</span>
        </button>
      </div>
      
      <div class="fonzie-summary-price">
        <span class="label">Total Price</span>
        <span class="price">${summary.price}</span>
      </div>
      
      <button class="fonzie-add-to-cart" type="button" ${!variant ? 'disabled' : ''}>
        ${!variant ? 'Configuration not available' : 'Add Car Kit to Cart'}
      </button>
    `;
    
    // Add edit listeners
    const editCards = summaryContainer.querySelectorAll('.fonzie-summary-card');
    editCards.forEach(card => {
      card.addEventListener('click', () => {
        const stepToEdit = parseInt(card.getAttribute('data-edit-step'));
        this.goToStep(stepToEdit);
      });
    });
    
    // Add to cart listener
    const addToCartBtn = summaryContainer.querySelector('.fonzie-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        this.addToCart();
      });
    }
  }
  
  /**
   * Add to cart
   */
  async addToCart() {
    const variant = this.engine.findMatchingVariant();
    
    if (!variant) {
      alert('Sorry, this configuration is not available.');
      return;
    }
    
    console.log('🛒 Adding to cart:', variant.id);
    
    // Show loading
    this.showLoading();
    
    try {
      // Add to cart via Shopify API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variant.id,
          quantity: 1
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const data = await response.json();
      console.log('✅ Added to cart:', data);
      
      // Track
      this.engine.trackEvent('kit_added_to_cart', variant.id, {
        color: this.engine.selections.color,
        size: this.engine.selections.size,
        sprayer: this.engine.selections.sprayer,
        finish: this.engine.selections.finish,
        price: this.engine.formatPrice(variant.price)
      });
      
      // Close drawer
      this.close();
      
      // Open cart drawer
      const cartToggle = document.querySelector('[aria-controls="Cart-Drawer"]');
      if (cartToggle) {
        cartToggle.click();
      }
      
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      alert('Sorry, there was an error adding to cart. Please try again.');
    } finally {
      this.hideLoading();
    }
  }
  
  /**
   * Show loading overlay
   */
  showLoading() {
    const overlay = this.drawer.querySelector('.fonzie-loading-overlay');
    if (overlay) {
      overlay.classList.add('active');
    }
  }
  
  /**
   * Hide loading overlay
   */
  hideLoading() {
    const overlay = this.drawer.querySelector('.fonzie-loading-overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
  }
  
  /**
   * Reset builder
   */
  reset() {
    this.engine.reset();
    this.goToStep(0);
    
    // Clear all selections in UI
    const allCards = this.drawer.querySelectorAll('.fonzie-color-card, .fonzie-option-card');
    allCards.forEach(card => {
      card.classList.remove('selected');
    });
  }
}

// Auto-initialize when engine is loaded
if (window.FonzieEngine) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.fonzieDrawerBuilder = new FonzieDrawerBuilder();
    });
  } else {
    window.fonzieDrawerBuilder = new FonzieDrawerBuilder();
  }
} else {
  console.warn('⚠️ Fonzie Drawer Builder: Engine not loaded yet');
}

console.log('✅ Fonzie Drawer Builder script loaded');

